<template>
  <footer class="bg-primary-900 text-secondary-300">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">

        <!-- Brand -->
        <div class="lg:col-span-1">
          <router-link to="/" class="flex items-center gap-3 mb-4">
            <img src="@/assets/jengahills.jpeg" alt="Jenga Hills" class="h-10 w-auto rounded" />
            <span class="text-white font-bold text-lg">Jenga Hills</span>
          </router-link>
          <p class="text-sm leading-relaxed text-secondary-400">
            Quality construction materials delivered to your site.
            Building Rwanda, one project at a time.
          </p>
        </div>

        <!-- Quick links -->
        <div>
          <h3 class="text-white font-semibold text-sm mb-4 uppercase tracking-wide">Shop</h3>
          <ul class="space-y-2 text-sm">
            <li><router-link to="/shop" class="hover:text-white transition-colors">All Products</router-link></li>
            <li><router-link to="/shop?category=cement-concrete" class="hover:text-white transition-colors">Cement & Concrete</router-link></li>
            <li><router-link to="/shop?category=steel-iron" class="hover:text-white transition-colors">Steel & Iron</router-link></li>
            <li><router-link to="/shop?category=roofing" class="hover:text-white transition-colors">Roofing</router-link></li>
          </ul>
        </div>

        <!-- Account -->
        <div>
          <h3 class="text-white font-semibold text-sm mb-4 uppercase tracking-wide">Account</h3>
          <ul class="space-y-2 text-sm">
            <template v-if="isAuthenticated">
              <li><router-link to="/profile" class="hover:text-white transition-colors">My Profile</router-link></li>
              <li><router-link to="/orders" class="hover:text-white transition-colors">My Orders</router-link></li>
              <li><router-link to="/account/settings" class="hover:text-white transition-colors">Settings</router-link></li>
              <li><router-link to="/cart" class="hover:text-white transition-colors">My Cart</router-link></li>
            </template>
            <template v-else>
              <li><router-link to="/login" class="hover:text-white transition-colors">Sign In</router-link></li>
              <li><router-link to="/register" class="hover:text-white transition-colors">Register</router-link></li>
              <li><router-link to="/cart" class="hover:text-white transition-colors">My Cart</router-link></li>
            </template>
          </ul>
        </div>

        <!-- Contact -->
        <div>
          <h3 class="text-white font-semibold text-sm mb-4 uppercase tracking-wide">Contact</h3>
          <ul class="space-y-2 text-sm">
            <li class="flex items-start gap-2">
              <svg class="w-4 h-4 mt-0.5 flex-shrink-0 text-accent-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <span>Kigali, Rwanda</span>
            </li>
            <li class="flex items-center gap-2">
              <svg class="w-4 h-4 flex-shrink-0 text-accent-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
              </svg>
              <span>+250 700 000 000</span>
            </li>
            <li class="flex items-center gap-2">
              <svg class="w-4 h-4 flex-shrink-0 text-accent-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              <span>info@jengahills.com</span>
            </li>
          </ul>
        </div>
      </div>

      <!-- Sell on Jenga Hills -->
      <div class="border-t border-primary-700 mt-10 pt-8 mb-6">
        <div class="bg-primary-800 rounded-2xl border border-primary-700 p-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div>
            <p class="text-white font-semibold mb-1">Are you a supplier or contractor?</p>
            <p class="text-secondary-400 text-sm">Join Jenga Hills as a vendor and reach thousands of builders across Rwanda.</p>
          </div>
          <router-link
            to="/vendor/register"
            class="flex-shrink-0 bg-accent-300 hover:bg-accent-400 text-white text-sm font-semibold px-5 py-2.5 rounded-lg transition-colors whitespace-nowrap"
          >
            Start Selling
          </router-link>
        </div>
      </div>

      <div class="border-t border-primary-700 pt-6 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-secondary-400">
        <p>&copy; {{ year }} Jenga Hills. All rights reserved.</p>
        <!-- Social links -->
        <div class="flex items-center gap-4">
          <a href="#" aria-label="Facebook" class="hover:text-white transition-colors">
            <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
              <path d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" />
            </svg>
          </a>
          <a href="#" aria-label="Twitter / X" class="hover:text-white transition-colors">
            <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
              <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
            </svg>
          </a>
          <a href="#" aria-label="LinkedIn" class="hover:text-white transition-colors">
            <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
              <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
            </svg>
          </a>
          <a href="#" aria-label="WhatsApp" class="hover:text-white transition-colors">
            <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
            </svg>
          </a>
        </div>
      </div>
    </div>
  </footer>
</template>

<script lang="ts">
import { defineComponent, computed } from 'vue'
import { useStore } from 'vuex'

export default defineComponent({
  name: 'AppFooter',
  setup () {
    const store = useStore()
    const year = computed(() => new Date().getFullYear())
    const isAuthenticated = computed(() => store.getters['auth/isAuthenticated'])
    return { year, isAuthenticated }
  }
})
</script>
